import { useRef, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { FiUploadCloud, FiDownload, FiFile } from "react-icons/fi";
import DefaultButton from "../Buttons/Default";
import { toast } from "react-toastify";
import { BASE_URL } from "../../utils/constants";
import "./Import.css";
import { useTranslation } from "react-i18next";
import { postForm } from '../../utils/helpers/api';

function ImportModal({ isOpen, toggle, module, section, onImportSuccess }) {
  const inputRef = useRef();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const moduleLabel = section.charAt(0).toUpperCase() + section.slice(1);

  const handleFile = (e) => setFile(e.target.files[0] || null);

  const handleDrop = (e) => {
    e.preventDefault();
    setFile(e.dataTransfer.files[0] || null);
  };

  const handleClose = () => {
    setFile(null);
    toggle();
  };

  const importModule = async () => {
    setLoading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await postForm(`/${module}/${section}/import`, formData);
      toast.success(t('common.importSuccess', { module: moduleLabel }));
      if (onImportSuccess) onImportSuccess();
      handleClose();
    } catch (error) {
      toast.error(t('common.importFailed', { module: moduleLabel }));
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal isOpen={isOpen} toggle={handleClose} centered>
      <ModalHeader toggle={handleClose}>{t('common.import')} {moduleLabel}</ModalHeader>

      <ModalBody>
        <p className="text-muted mb-3" style={{ fontSize: "0.85rem" }}>
          {t('common.uploadCsv', { module })}
        </p>

        <div
          className="import-drop-zone"
          onClick={() => inputRef.current.click()}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv"
            className="d-none"
            onChange={handleFile}
          />
          {file ? (
            <>
              <FiFile size={28} className="import-drop-icon" />
              <div className="import-drop-filename">{file.name}</div>
              <div className="import-drop-hint">{t('common.clickToChange')}</div>
            </>
          ) : (
            <>
              <FiUploadCloud size={28} className="import-drop-icon" />
              <div className="import-drop-label">{t('common.clickToBrowse')}</div>
              <div className="import-drop-hint">{t('common.csvOnly')}</div>
            </>
          )}
        </div>

        <a href={`${BASE_URL}/${module}/examples/${section}-example.csv`} target="_blank" download className="import-sample-link">
          <FiDownload size={13} /> {t('common.downloadSample')}
        </a>
      </ModalBody>

      <ModalFooter>
        <DefaultButton color="light" size="sm" outline onClick={handleClose}>{t('common.cancel')}</DefaultButton>
        <DefaultButton
          color="primary"
          size="sm"
          disabled={!file || loading}
          onClick={importModule}
          loading={loading}
        >
          {t('common.import')}
        </DefaultButton>
      </ModalFooter>
    </Modal>
  );
}

export default ImportModal;
